// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { DollarSign, CreditCard, TrendingUp, Calculator, Eye } from 'lucide-react';
import { toast } from 'sonner';

interface FinanceData {
  totalRevenue: number;
  totalCommissions: number;
  pendingCommissions: number;
  paidCommissions: number;
  platformRevenue: number;
  monthlyRevenue: Array<{ month: string; revenue: number; commissions: number }>;
  vendorCommissions: Array<{
    vendor: string;
    orders: number;
    revenue: number;
    commissionRate: number;
    commissionOwed: number;
    commissionPaid: number;
  }>;
  recentOrders: Array<{
    id: string;
    vendor: string;
    amount: number;
    commission: number;
    status: string;
    date: string;
  }>;
}

const VideoFinanceTab: React.FC = () => {
  const [finance, setFinance] = useState<FinanceData>({
    totalRevenue: 0,
    totalCommissions: 0,
    pendingCommissions: 0,
    paidCommissions: 0,
    platformRevenue: 0,
    monthlyRevenue: [],
    vendorCommissions: [],
    recentOrders: []
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFinanceData();
  }, []);

  const fetchFinanceData = async () => {
    try {
      setIsLoading(true);

      // Get video orders with vendor commission rates
      const { data: orders, error: ordersError } = await supabase
        .from('video_orders')
        .select(`
          *,
          video_vendor_profiles:vendor_id (
            id,
            business_name,
            profiles:user_id (name, commission_rate)
          )
        `);

      if (ordersError) throw ordersError;

      if (!orders || orders.length === 0) {
        setIsLoading(false);
        return;
      }

      // Calculate basic financial metrics
      const totalRevenue = orders.reduce((sum, order) => sum + (order.total_amount_cents || 0), 0) / 100;
      
      // Calculate commissions for each order
      const ordersWithCommissions = orders.map(order => {
        const revenue = (order.total_amount_cents || 0) / 100;
        const commissionRate = order.video_vendor_profiles?.profiles?.commission_rate || 10; // Default 10%
        const commission = revenue * (commissionRate / 100);
        return {
          ...order,
          revenue,
          commission,
          commissionRate
        };
      });

      const totalCommissions = ordersWithCommissions.reduce((sum, order) => sum + order.commission, 0);
      const completedOrders = ordersWithCommissions.filter(order => order.status === 'completed');
      const paidCommissions = completedOrders.reduce((sum, order) => sum + order.commission, 0);
      const pendingCommissions = totalCommissions - paidCommissions;
      const platformRevenue = totalRevenue - totalCommissions;

      // Monthly revenue data
      const monthlyRevenue = generateMonthlyFinanceData(ordersWithCommissions);

      // Vendor commission breakdown
      interface VendorStats {
        orders: number;
        revenue: number;
        commissionRate: number;
        commissionOwed: number;
        commissionPaid: number;
      }

      const vendorStats: Record<string, VendorStats> = {};

      ordersWithCommissions.forEach(order => {
        const vendorName = order.video_vendor_profiles?.profiles?.name || 
                         order.video_vendor_profiles?.business_name || 
                         'Unknown Vendor';
        if (!vendorStats[vendorName]) {
          vendorStats[vendorName] = {
            orders: 0,
            revenue: 0,
            commissionRate: order.commissionRate,
            commissionOwed: 0,
            commissionPaid: 0
          };
        }
        vendorStats[vendorName].orders += 1;
        vendorStats[vendorName].revenue += order.revenue;
        vendorStats[vendorName].commissionOwed += order.commission;
        if (order.status === 'completed') {
          vendorStats[vendorName].commissionPaid += order.commission;
        }
      });

      const vendorCommissions = Object.entries(vendorStats).map(([vendor, stats]) => ({
        vendor,
        orders: stats.orders,
        revenue: stats.revenue,
        commissionRate: stats.commissionRate,
        commissionOwed: stats.commissionOwed,
        commissionPaid: stats.commissionPaid
      }));

      // Recent orders for transaction history
      const recentOrders = ordersWithCommissions
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10)
        .map(order => ({
          id: order.id,
          vendor: order.video_vendor_profiles?.profiles?.name || 
                 order.video_vendor_profiles?.business_name || 'Unknown',
          amount: order.revenue,
          commission: order.commission,
          status: order.status,
          date: new Date(order.created_at).toLocaleDateString()
        }));

      setFinance({
        totalRevenue,
        totalCommissions,
        pendingCommissions,
        paidCommissions,
        platformRevenue,
        monthlyRevenue,
        vendorCommissions,
        recentOrders
      });

    } catch (error) {
      console.error('Error fetching finance data:', error);
      toast.error('Failed to load finance data');
    } finally {
      setIsLoading(false);
    }
  };

  const generateMonthlyFinanceData = (orders: any[]) => {
    const months = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      
      const monthOrders = orders.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate.getMonth() === date.getMonth() && orderDate.getFullYear() === date.getFullYear();
      });
      
      months.push({
        month: monthName,
        revenue: monthOrders.reduce((sum, order) => sum + order.revenue, 0),
        commissions: monthOrders.reduce((sum, order) => sum + order.commission, 0)
      });
    }
    
    return months;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading finance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Financial Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${finance.totalRevenue.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">Gross revenue</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Platform Revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${finance.platformRevenue.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">After commissions</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Commissions Paid</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${finance.paidCommissions.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">Completed orders</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Commissions</CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${finance.pendingCommissions.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">Awaiting completion</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue vs Commissions Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Revenue & Commissions</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={finance.monthlyRevenue}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => [`$${value}`, '']} />
              <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
              <Bar dataKey="commissions" fill="#10b981" name="Commissions" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vendor Commission Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Vendor Commission Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Owed</TableHead>
                  <TableHead>Paid</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {finance.vendorCommissions.slice(0, 5).map((vendor, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{vendor.vendor}</TableCell>
                    <TableCell>{vendor.commissionRate}%</TableCell>
                    <TableCell>${vendor.commissionOwed.toFixed(2)}</TableCell>
                    <TableCell className="text-green-600">${vendor.commissionPaid.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {finance.recentOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="font-medium">{order.vendor}</TableCell>
                    <TableCell>${order.amount.toFixed(2)}</TableCell>
                    <TableCell>${order.commission.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={order.status === 'completed' ? 'default' : 'secondary'}>
                        {order.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VideoFinanceTab;